import { Trans } from '@/components/Trans'
import {
  DAILY_PURCHASE_OPTIONS,
  DURATION,
  MONTHLY_PURCHASE_OPTIONS,
  PurchaseOption,
} from '@/consts/order'
import { cn } from '@/utils/style'

export type PurchaseValue = number | 'custom'

interface PurchaseForSectionProps {
  duration: string
  purchaseFor: PurchaseValue
  subscriptionTotalDays?: number
  onChange: (payload: { purchaseFor: PurchaseValue; totalDays: number }) => void
  className?: string
}

export function PurchaseForSection({
  duration,
  purchaseFor,
  subscriptionTotalDays,
  onChange,
  className,
}: PurchaseForSectionProps) {
  const isDaily = duration === DURATION.DAILY
  const options = isDaily ? DAILY_PURCHASE_OPTIONS : MONTHLY_PURCHASE_OPTIONS

  const handleFixedClick = (opt: PurchaseOption) => {
    if (opt.value === 'custom') {
      const defaultDays = isDaily ? 1 : 30
      onChange({ purchaseFor: 'custom', totalDays: defaultDays })
      return
    }
    onChange({ purchaseFor: opt.value, totalDays: opt.value as number })
  }

  const handleCustomChange = (value: string) => {
    if (value === '') {
      onChange({ purchaseFor: 'custom', totalDays: 0 })
      return
    }

    const num = Number(value)
    if (Number.isNaN(num)) return

    const max = isDaily ? 180 : 6

    const clamped = Math.min(Math.max(num, 0), max)

    const totalDays = isDaily ? clamped : clamped * 30
    onChange({ purchaseFor: 'custom', totalDays })
  }

  const customDisplayValue = (() => {
    if (!subscriptionTotalDays || subscriptionTotalDays <= 0) {
      return ''
    }
    return isDaily ? subscriptionTotalDays : Math.max(1, Math.round(subscriptionTotalDays / 30))
  })()

  return (
    <div className={cn('mt-8 px-8', className)}>
      <div className="t-sb mb-2 text-secondary">
        <Trans i18nKey="create.purchaseFor">Subscription Duration</Trans>
      </div>

      <div className="flex flex-wrap gap-3">
        {options.map((opt) => {
          const isCustom = opt.value === 'custom'
          const isSelected = purchaseFor === opt.value

          if (isCustom) {
            if (!isSelected) {
              return (
                <button
                  key="custom"
                  type="button"
                  className={cn(
                    'relative h-11 px-8 rounded-xl border text-sm flex items-center gap-2',
                    'border-neutral-200 bg-panel-white text-secondary hover:bg-neutral-50',
                  )}
                  onClick={() => {
                    const defaultDays = isDaily ? 1 : 30
                    onChange({ purchaseFor: 'custom', totalDays: defaultDays })
                  }}
                >
                  <span className="t-sb">
                    <Trans i18nKey="create.PurchaseForSection.custom">Custom</Trans>
                  </span>
                  <span className="t-xs text-tertiary">
                    {isDaily ? (
                      <Trans i18nKey="create.PurchaseForSection.days">Days</Trans>
                    ) : (
                      <Trans i18nKey="create.PurchaseForSection.months">Months</Trans>
                    )}
                  </span>
                </button>
              )
            }

            return (
              <div
                key="custom"
                className={cn(
                  'relative h-11 px-4 rounded-xl border text-sm flex items-center gap-3',
                  'border-secondary bg-highlight/10 text-highlight font-semibold',
                )}
              >
                <span className="t-sb">
                  <Trans i18nKey="create.PurchaseForSection.custom">Custom</Trans>
                </span>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    className="w-[80px] h-8 rounded-md border border-neutral-200 bg-panel-white px-2 text-sm text-primary focus:outline-none focus:ring-1 focus:ring-secondary"
                    min={1}
                    max={isDaily ? 180 : 6}
                    value={customDisplayValue}
                    onChange={(e) => handleCustomChange(e.target.value)}
                    onBlur={() => {
                      if (!subscriptionTotalDays || subscriptionTotalDays <= 0) {
                        const defaultDays = isDaily ? 1 : 1
                        onChange({
                          purchaseFor: 'custom',
                          totalDays: isDaily ? defaultDays : defaultDays * 30,
                        })
                      }
                    }}
                  />
                  <span className="t-xs text-secondary">
                    {isDaily ? (
                      <Trans i18nKey="create.PurchaseForSection.days">Days</Trans>
                    ) : (
                      <Trans i18nKey="create.PurchaseForSection.months">Months</Trans>
                    )}
                  </span>
                </div>
              </div>
            )
          }

          return (
            <button
              key={opt.value}
              type="button"
              className={cn(
                'relative h-11 px-8 rounded-xl border text-sm flex items-center gap-2',
                isSelected
                  ? 'border-secondary bg-highlight/10 text-primary font-semibold'
                  : 'border-neutral-200 bg-panel-white text-secondary hover:bg-neutral-50',
              )}
              onClick={() => handleFixedClick(opt)}
            >
              <span className="t-sb">
                {isDaily ? (opt.value as number) : Math.round((opt.value as number) / 30)}{' '}
                <Trans
                  i18nKey={
                    isDaily
                      ? (opt.value as number) === 1
                        ? 'create.PurchaseForSection.day'
                        : 'create.PurchaseForSection.days'
                      : Math.round((opt.value as number) / 30) === 1
                        ? 'create.PurchaseForSection.month'
                        : 'create.PurchaseForSection.months'
                  }
                >
                  {isDaily
                    ? (opt.value as number) === 1
                      ? 'Day'
                      : 'Days'
                    : Math.round((opt.value as number) / 30) === 1
                      ? 'Month'
                      : 'Months'}
                </Trans>
              </span>
            </button>
          )
        })}
      </div>

      {isDaily ? (
        <div className="t-xs text-tertiary mt-2">
          <Trans i18nKey="create.customPeriodMustBeBetweenDays">
            Custom subscription duration must be between 1 and 180 days.
          </Trans>
        </div>
      ) : (
        <div className="t-xs text-tertiary mt-2">
          <Trans i18nKey="create.customPeriodMustBeBetweenMonths">
            Custom subscription duration must be between 1 and 6 months.
          </Trans>
        </div>
      )}
    </div>
  )
}
